/**
 * The device layer: what a headband is to the capture flow, whichever
 * hardware (or none) is behind it.
 *
 * Three implementations: `WebBluetoothClassicBrainBit` (the 4-channel unit over
 * raw BLE, `brainbitClassic.ts`), `WebBluetoothBrainBitPro` (the 8-channel Pro,
 * `brainbitPro.ts`, protocol in `docs/pro-protocol.md`) and `SyntheticHeadband`
 * (a fixture with scenario knobs, `synthetic.ts`). The screen and the phase
 * machine talk only to this interface, so the phase tests run against the
 * synthetic one and the same screen drives the real ones.
 */

export type HeadbandFamily = 'brainbit' | 'brainbit_pro' | 'synthetic'

export interface HeadbandInfo {
  family: HeadbandFamily
  /** Human name as advertised, e.g. "BrainBit". */
  name: string
  /** Hardware channels in the order the device reports them. */
  channels: readonly string[]
  serial: string | null
  battery: number | null
  /** The vendor's `sens_family` word, written into `meta.json`. */
  sensFamily: string
  /**
   * True when contact quality is estimated from the signal rather than measured.
   * The classic starts true and turns false when its measured readout delivers,
   * true again if it falls back to the estimate (R5); the value at the end of
   * fitting is what `meta.json` records.
   */
  estimatedQuality: boolean
  /** Sampling rate in Hz. Every BrainBit profile the backend knows is 250. */
  samplingRate: number
}

/** One decoded sample: one value per hardware channel, in `channels` order, in µV. */
export interface Sample {
  /** The device's own counter where it has one, otherwise a running index. */
  pack: number
  values: number[]
}

export interface SamplesEvent {
  /** `performance.now()` when the frame arrived. */
  at: number
  samples: Sample[]
}

export interface ResistEvent {
  at: number
  /** kΩ per channel; `null`/NaN means no reading. */
  readings: Record<string, number | null>
  /** True when the readings are estimated from the signal. */
  estimated: boolean
}

export interface StatusEvent {
  at: number
  connected: boolean
  battery: number | null
}

/**
 * The data can no longer be trusted: a lost, repeated or reordered frame, a
 * malformed one, no first frame, or a contact lost mid-signal. The attempt is
 * over; the driver emits nothing more until the next start.
 */
export interface FaultEvent {
  at: number
  message: string
  /** Present when the adapter directly observed a contact leave the skin. */
  reason?: 'contact_lost'
}

export interface HeadbandEvents {
  samples: SamplesEvent
  resist: ResistEvent
  status: StatusEvent
}

export type HeadbandEventName = keyof HeadbandEvents

export interface Headband {
  readonly info: HeadbandInfo | null
  /** Pair and connect. Must be called from a user gesture for Web Bluetooth. */
  connect(): Promise<HeadbandInfo>
  /** Stream signal frames (`samples`). Stops the resist stream where the two are exclusive. */
  startSignal(): Promise<void>
  stopSignal(): Promise<void>
  /** Stream contact readings (`resist`). Where the resistance readout is unavailable or fails, this estimates from the signal. */
  startResist(): Promise<void>
  stopResist(): Promise<void>
  on<K extends HeadbandEventName>(event: K, handler: (payload: HeadbandEvents[K]) => void): () => void
  /**
   * Faults, from a driver that can detect them (the Pro). Optional, and outside
   * `HeadbandEvents`, so a headband that cannot tell does not have to pretend.
   */
  onFault?(handler: (payload: FaultEvent) => void): () => void
  /**
   * The contact check at Start, from a driver whose signal can show a lifted
   * contact (the Pro): the channels that are lifted now. Signal must be streaming.
   */
  checkContacts?(): Promise<string[]>
  disconnect(): Promise<void>
}

/** A tiny typed emitter shared by the implementations. */
export class Emitter {
  private handlers: { [K in HeadbandEventName]: Set<(p: HeadbandEvents[K]) => void> } = {
    samples: new Set(),
    resist: new Set(),
    status: new Set(),
  }

  on<K extends HeadbandEventName>(event: K, handler: (payload: HeadbandEvents[K]) => void): () => void {
    this.handlers[event].add(handler)
    return () => {
      this.handlers[event].delete(handler)
    }
  }

  emit<K extends HeadbandEventName>(event: K, payload: HeadbandEvents[K]): void {
    for (const h of [...this.handlers[event]]) h(payload)
  }

  clear(): void {
    this.handlers.samples.clear()
    this.handlers.resist.clear()
    this.handlers.status.clear()
  }
}

export class HeadbandError extends Error {
  readonly code: 'unsupported' | 'cancelled' | 'not_found' | 'connect_failed' | 'protocol'
  constructor(code: HeadbandError['code'], message: string) {
    super(message)
    this.name = 'HeadbandError'
    this.code = code
  }
}

/** Web Bluetooth is Chrome and Edge, over HTTPS or localhost, and nowhere else. */
export function webBluetoothAvailable(nav: Navigator | undefined = typeof navigator === 'undefined' ? undefined : navigator): boolean {
  return Boolean(nav && 'bluetooth' in nav && nav.bluetooth)
}
